<template>
  <PageLayout :title="$t('admin.transactions_title')" :subtitle="$t('admin.transactions_subtitle')">
    <ContentCard>
      <div class="d-flex flex-wrap mb-3 gap-2">
        <select v-model="statusFilter" class="form-control form-control-sm" style="max-width:160px" @change="load">
          <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
        <select v-model="providerFilter" class="form-control form-control-sm" style="max-width:160px" @change="load">
          <option value="">{{ $t('admin.all_providers') }}</option>
          <option value="paypal">{{ $t('billing.provider_paypal') }}</option>
          <option value="pakasir">{{ $t('billing.provider_local') }}</option>
        </select>
        <button class="btn btn-sm btn-primary" @click="load">{{ $t('common.refresh') }}</button>
      </div>

      <div v-if="loading" class="text-muted">{{ $t('common.loading') }}</div>
      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>{{ $t('global.id') }}</th>
              <th>{{ $t('global.user') }}</th>
              <th>{{ $t('global.plan') }}</th>
              <th>{{ $t('global.provider') }}</th>
              <th>{{ $t('global.status') }}</th>
              <th>{{ $t('global.period') }}</th>
              <th>{{ $t('admin.order_sub_id') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sub in subscriptions" :key="sub.id">
              <td>{{ sub.id }}</td>
              <td>
                <div>{{ sub.user?.name || '-' }}</div>
                <small class="text-muted">{{ sub.user?.email }}</small>
              </td>
              <td>{{ sub.plan?.name || '-' }}</td>
              <td>{{ providerLabel(sub.payment_provider) }}</td>
              <td><span class="badge" :class="statusClass(sub.status)">{{ statusLabel(sub.status) }}</span></td>
              <td>
                <small v-if="sub.current_period_end">
                  {{ formatDate(sub.current_period_start) }} → {{ formatDate(sub.current_period_end) }}
                </small>
                <span v-else>-</span>
              </td>
              <td>
                <small class="text-muted d-block">{{ sub.paypal_subscription_id || sub.pakasir_order_id || '-' }}</small>
              </td>
              <td>
                <button
                  v-if="sub.status === 'active'"
                  class="btn btn-xs btn-outline-danger"
                  @click="onCancel(sub)"
                >
                  {{ $t('admin.cancel_subscription') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="text-muted small mb-0 mt-2">{{ $t('common.total_count', { count: subscriptionsTotal }) }}</p>
    </ContentCard>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import i18nPage from '@/mixins/i18nPage'

export default {
  mixins: [i18nPage],
  data() {
    return {
      statusFilter: '',
      providerFilter: '',
    }
  },
  computed: {
    ...mapGetters('admin', ['subscriptions', 'subscriptionsTotal', 'loading']),
    statusOptions() {
      return [
        { value: '', label: this.$t('admin.all_status') },
        { value: 'active', label: this.$t('status.active') },
        { value: 'pending', label: this.$t('status.pending') },
        { value: 'cancelled', label: this.$t('status.cancelled') },
        { value: 'expired', label: this.$t('status.expired') },
      ]
    },
  },
  async created() {
    await this.load()
  },
  methods: {
    ...mapActions('admin', ['fetchSubscriptions', 'cancelSubscription']),
    async load() {
      await this.fetchSubscriptions({
        status: this.statusFilter,
        provider: this.providerFilter,
        page: 1,
        limit: 50,
      })
    },
    formatDate(v) {
      if (!v) return '-'
      return new Date(v).toLocaleDateString(this.intlLocale)
    },
    providerLabel(provider) {
      const map = {
        paypal: this.$t('billing.provider_paypal'),
        pakasir: this.$t('billing.provider_local'),
      }
      return map[provider] || provider || '-'
    },
    statusClass(status) {
      const map = {
        active: 'badge-success',
        pending: 'badge-warning',
        cancelled: 'badge-secondary',
        expired: 'badge-danger',
      }
      return map[status] || 'badge-light'
    },
    async onCancel(sub) {
      const ok = await this.$confirm(this.$t('admin.cancel_sub_confirm_detail', { id: sub.id, email: sub.user?.email }), {
        danger: true,
      })
      if (!ok) return
      try {
        await this.cancelSubscription(sub.id)
        this.$toast?.success?.(this.$t('admin.sub_cancelled'))
        await this.load()
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || this.$t('admin.cancel_failed'))
      }
    },
  },
}
</script>

<style scoped>
.gap-2 > * { margin-right: 0.5rem; margin-bottom: 0.5rem; }
</style>
